import React, { Suspense, useEffect, useRef, useState } from 'react';
import { AccessibilityHelp } from '../../common/components/AccessibilityHelp';
import { AccessibilityOptions } from '../../common/components/AccessibilityOptions';
import { appendCdnToImagePath } from '../../common/utils/appendCdnToImagePath';
import { BaseStyles } from '../styles/base';
import { DefaultQuestionTheme, QuestionTheme } from '../../survey/styles/mfsTheme';
import { faCog, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCookie } from '../../common/utils/cookies';
import { getExperienceInitCookie } from '../../common/utils/experienceInitCookie';
import { Logo } from './Logo';
import { ThemeProvider } from '@material-ui/core/styles';
import { useBasicDetails } from '../../store/storeApi';
import { useTranslation } from 'react-i18next';
import { UtilityLinkStyles } from '../styles/utilityLinks';
import {
  AppBar,
  Grid,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  IconButton,
  Divider,
  Box,
  Button,
  Typography,
} from '@material-ui/core';
const ExitScreen = React.lazy(() => import('../../survey/components/instructions/ExitScreen'));

/** Header Component */
const AssessmentHeader = (): JSX.Element => {
  const {
    template,
    headerOptions,
    headerOptions: { accessibility, selectedTheme },
    updateHeaderOptions,
  } = useBasicDetails();

  const { settings } = template;
  const { utilityBar } = settings;

  const { t } = useTranslation();
  const classes = BaseStyles(settings);
  const utilityLinkClass = UtilityLinkStyles(utilityBar);
  const [showAccessibilityHelpPage, setShowAccessibilityHelpPage] = useState<boolean>(false);
  const [exit, setExit] = useState<boolean>(false);

  let companyLogo = '';
  if (settings.utilityBar.logo) {
    companyLogo = settings.utilityBar.logo.base64logo
      ? settings.utilityBar.logo.base64logo
      : appendCdnToImagePath(settings.utilityBar.logo.path);
  }

  // Deadline date passed
  const expCookie = getExperienceInitCookie();

  // Preview Project
  const isPreviewProject = (expCookie && expCookie.previewProject) || getCookie('isPreview') === 'true' || false;

  /** Start: Setting Menu List handlers */
  const settingsAnchorRef = useRef<HTMLButtonElement>(null);
  const [openSettings, setOpenSettings] = useState(false);

  const handleToggleSettings = (): void => {
    setOpenSettings((prevOpen) => !prevOpen);
  };

  const handleCloseSettings = (event: React.MouseEvent<Document, MouseEvent>): void => {
    if (null !== settingsAnchorRef.current && settingsAnchorRef.current.contains(event.currentTarget)) {
      return;
    }
    setOpenSettings(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpenSettings = useRef(openSettings);
  useEffect(() => {
    if (prevOpenSettings.current === true && openSettings === false && null !== settingsAnchorRef.current) {
      settingsAnchorRef.current.focus();
    }

    prevOpenSettings.current = openSettings;
  }, [openSettings]);
  /** End: Setting Menu List handlers */

  const onThemeChange = (event: React.ChangeEvent<{ value: string }>): void => {
    const selectedThemeId = event.target.value;
    updateHeaderOptions({ ...headerOptions, ...{ selectedTheme: parseInt(selectedThemeId) } });
    setOpenSettings(false);
  };

  /** exit handler */
  const onExitClick = (event: React.KeyboardEvent | React.MouseEvent): void => {
    if (event.type === 'keyup') {
      return;
    }
    if (
      event.type === 'keydown' &&
      !((event as React.KeyboardEvent).keyCode === 13 || (event as React.KeyboardEvent).keyCode === 32)
    ) {
      return;
    }
    event.preventDefault();
    setExit(!exit);
    // updateHeaderOptions({ ...headerOptions, ...{ exit: true } });
  };

  const onCancel = (): void => {
    setExit(false);
  };

  const onContinueClick = (): void => {
    setShowAccessibilityHelpPage(false);
  };

  const onHelpClick = (): void => {
    setOpenSettings(false);
    setShowAccessibilityHelpPage(true);
  };

  return (
    <>
      {!showAccessibilityHelpPage ? (
        <AppBar color={'default'} position={'fixed'} className={classes.header}>
          <Box display="flex" alignItems="center" className={classes.mobile}>
            <>
              <Box className={classes.logo}>
                {settings.utilityBar.logo && <Logo src={companyLogo} alt={settings.utilityBar.logo.name} />}
              </Box>
              <Box ml={2}>
                <IconButton
                  ref={settingsAnchorRef}
                  className={utilityLinkClass.settingsIcon}
                  aria-controls={openSettings ? 'settings-list-grow' : undefined}
                  aria-haspopup="true"
                  onClick={handleToggleSettings}
                  color="inherit"
                  id="accessibilityTheme">
                  <Typography variant="srOnly" className="srIcon">
                    {t('settings')}
                  </Typography>
                  <FontAwesomeIcon spin={openSettings} icon={faCog} />
                </IconButton>

                <Popper
                  open={openSettings}
                  anchorEl={settingsAnchorRef.current}
                  role="listbox"
                  transition
                  disablePortal
                  placement="bottom-end">
                  {({ TransitionProps, placement }): JSX.Element => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                      }}>
                      <Paper>
                        <ClickAwayListener onClickAway={handleCloseSettings}>
                          <Grid item className={classes.desktopAccessibilitySettings}>
                            <Grid>
                              <Grid item xs={12}>
                                <AccessibilityOptions
                                  options={headerOptions.accessibility}
                                  onThemeChange={onThemeChange}
                                  onHelpClick={onHelpClick}
                                  selectedTheme={headerOptions.selectedTheme}
                                  aria-hidden="true"
                                />
                              </Grid>
                            </Grid>
                            <Divider light={true} />
                          </Grid>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </Box>
              {!isPreviewProject && (
                <Button
                  onClick={onExitClick}
                  color="inherit"
                  disabled={isPreviewProject}
                  aria-hidden={isPreviewProject}
                  aria-disabled="false"
                  aria-label={t('exitButton')}
                  id="assessmentExit"
                  className={utilityLinkClass.userProfile}
                  startIcon={<FontAwesomeIcon icon={faSignOutAlt} />}>
                  <Box pr={1}>{t('exitButton')}</Box>
                </Button>
              )}
            </>
          </Box>
        </AppBar>
      ) : (
        <AccessibilityHelp onContinueClick={onContinueClick} />
      )}
      {exit && (
        <ThemeProvider
          theme={selectedTheme === 0 ? DefaultQuestionTheme(settings) : QuestionTheme(accessibility[selectedTheme])}>
          <Suspense fallback={<></>}>
            <ExitScreen onCancel={onCancel} />
          </Suspense>
        </ThemeProvider>
      )}
    </>
  );
};

export default AssessmentHeader;
